import React, { useState, useEffect } from "react";
import { Heart, Stethoscope, Activity, Pill, Search, Filter, CheckCircle2, AlertTriangle } from "lucide-react";

import { useConsultationWizardState } from "../../../hooks/useConsultationWizardState";

/* eslint-disable @typescript-eslint/no-empty-object-type */
interface MedicalConditionsStepProps {
  // Add properties here if needed
}
/* eslint-enable @typescript-eslint/no-empty-object-type */

const CheckboxField: React.FC<{
  label: string;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  category: string;
}> = ({ label, checked, onChange, category }) => {
  const getCategoryColor = (cat: string) => {
    switch (cat) {
      case "cardiovascular":
        return "from-red-50 to-rose-50 border-red-100";
      case "endocrine":
        return "from-purple-50 to-violet-50 border-purple-100";
      case "respiratory":
        return "from-blue-50 to-cyan-50 border-blue-100";
      case "neurological":
        return "from-green-50 to-emerald-50 border-green-100";
      case "gynecological":
        return "from-pink-50 to-fuchsia-50 border-pink-100";
      case "infectious":
        return "from-orange-50 to-amber-50 border-orange-100";
      case "other":
        return "from-gray-50 to-slate-50 border-gray-100";
      default:
        return "from-blue-50 to-indigo-50 border-blue-100";
    }
  };

  return (
    <label
      className={`group flex items-center space-x-3 p-4 rounded-xl border-2 bg-gradient-to-br ${getCategoryColor(
        category
      )} cursor-pointer transition-all duration-300 hover:shadow-md hover:scale-[1.02] ${checked ? "ring-2 ring-blue-400 shadow-lg" : ""
        }`}
    >
      <div className="relative">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="sr-only"
        />
        <div
          className={`w-5 h-5 rounded-lg border-2 transition-all duration-200 flex items-center justify-center ${checked
            ? "bg-gradient-to-br from-blue-500 to-blue-600 border-blue-600"
            : "border-gray-300 bg-white group-hover:border-blue-400"
            }`}
        >
          {checked && (
            <CheckCircle2 className="w-3 h-3 text-white animate-in zoom-in-50 duration-200" />
          )}
        </div>
      </div>
      <span
        className={`text-sm font-medium transition-colors duration-200 ${checked ? "text-blue-700" : "text-gray-700 group-hover:text-gray-900"
          }`}
      >
        {label}
      </span>
    </label>
  );
};

const MedicalConditionsStep: React.FC<MedicalConditionsStepProps> = () => {
  const { formData, updateFormData } = useConsultationWizardState();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [, setAnimationDelay] = useState(0);

  useEffect(() => {
    setAnimationDelay(0);
  }, []);

  const medicalConditionsData = [
    { key: "hypertensionArterielle", label: "Hypertension Artérielle", category: "cardiovascular" },
    { key: "maladieCardiaque", label: "Maladie Cardiaque", category: "cardiovascular" },
    { key: "cholesterolEleve", label: "Cholestérol Élevé", category: "cardiovascular" },
    { key: "avc", label: "AVC", category: "cardiovascular" },
    { key: "diabete", label: "Diabète", category: "endocrine" },
    { key: "maladieThyroidienne", label: "Maladie Thyroïdienne", category: "endocrine" },
    { key: "drepanocytose", label: "Drépanocytose", category: "other" },
    { key: "neurologique", label: "Troubles Neurologiques", category: "neurological" },
    { key: "toux", label: "Toux Chronique", category: "respiratory" },
    { key: "douleursMammaires", label: "Douleurs Mammaires", category: "gynecological" },
    { key: "anomalieUterine", label: "Anomalie Utérine", category: "gynecological" },
    { key: "respirationSifflante", label: "Respiration Sifflante", category: "respiratory" },
    { key: "maladiesInfectieuses", label: "Maladies Infectieuses", category: "infectious" },
    { key: "tuberculose", label: "Tuberculose", category: "infectious" },
    { key: "asthme", label: "Asthme", category: "respiratory" },
    { key: "arthrite", label: "Arthrite", category: "other" },
    { key: "cancer", label: "Cancer", category: "other" },
    { key: "anemie", label: "Anémie", category: "other" },
    { key: "defautGenetique", label: "Défaut Génétique", category: "other" },
    { key: "sinusite", label: "Sinusite", category: "respiratory" },
    { key: "vertige", label: "Vertiges", category: "neurological" },
    { key: "problemesMentaux", label: "Problèmes Mentaux", category: "neurological" },
    { key: "allergie", label: "Allergies", category: "other" },
    { key: "chirurgieGyneco", label: "Chirurgie Gynécologique", category: "gynecological" },
    { key: "infertilite", label: "Infertilité", category: "gynecological" },
    { key: "vih", label: "VIH", category: "infectious" },
    { key: "cyclesIrreguliers", label: "Cycles Irréguliers", category: "gynecological" },
    { key: "fatigue", label: "Fatigue Chronique", category: "other" },
  ];

  const categories = [
    { key: "all", label: "Toutes", icon: Heart, color: "text-gray-600" },
    { key: "cardiovascular", label: "Cardiovasculaire", icon: Heart, color: "text-red-500" },
    { key: "respiratory", label: "Respiratoire", icon: Activity, color: "text-blue-500" },
    { key: "endocrine", label: "Endocrinien", icon: Pill, color: "text-purple-500" },
    { key: "neurological", label: "Neurologique", icon: Activity, color: "text-green-500" },
    { key: "gynecological", label: "Gynécologique", icon: Heart, color: "text-pink-500" },
    { key: "infectious", label: "Infectieux", icon: AlertTriangle, color: "text-orange-500" },
    { key: "other", label: "Autres", icon: Stethoscope, color: "text-gray-500" },
  ];

  const filteredConditions = medicalConditionsData.filter((condition) => {
    const matchesSearch = condition.label.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || condition.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const selectedCount = medicalConditionsData.filter(
    (condition) => formData.conditionsMedicales[condition.key]
  ).length;

  const handleMedicamentsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateFormData("medicaments", e.target.value);
  };

  return (
    <div className="space-y-8">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(8)].map((_, i) => {
          const icons = [Heart, Stethoscope, Activity, Pill];
          const Icon = icons[i % icons.length];
          const colors = ['text-red-200', 'text-blue-200', 'text-green-200', 'text-purple-200'];

          return (
            <div
              key={i}
              className={`absolute ${colors[i % colors.length]} opacity-20 animate-float`}
            >
              <Icon className="w-4 h-4" />
            </div>
          );
        })}
      </div>

      <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-3xl p-6 sm:p-8 shadow-2xl border border-blue-100 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 rounded-t-3xl"></div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div className="flex items-center mb-4 md:mb-0">
            <div className="bg-gradient-to-br from-blue-100 to-indigo-100 p-3 rounded-2xl mr-4">
              <Heart className="text-blue-600" size={28} />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Conditions Médicales
              </h3>
              <p className="text-gray-600 font-medium">
                Historique médical du patient
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="bg-white/80 backdrop-blur-sm px-4 py-2 rounded-xl border border-white/50 shadow-lg">
              <span className="text-sm text-gray-600 font-medium">Sélectionnées: </span>
              <span className="text-lg font-bold text-blue-600">{selectedCount}</span>
            </div>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50 mb-8">
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher une condition médicale..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="text-gray-500" size={20} />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white"
              >
                {categories.map(category => (
                  <option key={category.key} value={category.key}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {categories.map((category) => {
              const Icon = category.icon;
              const isSelected = selectedCategory === category.key;

              return (
                <button
                  key={category.key}
                  onClick={() => setSelectedCategory(category.key)}
                  className={`flex items-center px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${isSelected
                    ? 'bg-blue-500 text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  <Icon className="mr-1" size={14} />
                  {category.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/40 mb-8">
          <p className="text-gray-700 mb-6 font-medium">
            Cochez toutes les conditions que le patient a ou a eu :
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredConditions.map((condition) => (
              <div
                key={condition.key}
                className="animate-in slide-in-from-bottom-4 duration-300"
              >
                <CheckboxField
                  label={condition.label}
                  checked={!!formData.conditionsMedicales[condition.key]}
                  onChange={(e) =>
                    updateFormData("conditionsMedicales", condition.key, e.target.checked)
                  }
                  category={condition.category}
                />
              </div>
            ))}
          </div>

          {filteredConditions.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <Search size={48} />
              </div>
              <p className="text-gray-500 font-medium">Aucune condition trouvée</p>
              <p className="text-gray-400 text-sm">Essayez un autre terme de recherche</p>
            </div>
          )}
        </div>

        {/* ✅ Ajout: Champ Allergies */}
        <div className="bg-gradient-to-br from-red-50 to-pink-50 p-4 rounded-xl border border-red-100 mb-6">
          <label className="flex items-center text-sm font-semibold text-red-800 mb-2">
            <AlertTriangle className="mr-2 text-red-600" size={16} />
            🚨 Allergies connues
          </label>
          <input
            type="text"
            value={formData.patientInfo?.allergies || ""}
            onChange={(e) =>
              updateFormData("patientInfo", "allergies", e.target.value)
            }
            className="w-full px-4 py-3 border-2 border-red-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
            placeholder="Ex: Pénicilline, arachides, pollen, lactose..."
          />
          <p className="text-xs text-red-600 mt-2">
            Important : Liste toutes les allergies connues du patient (médicaments, aliments, environnement)
          </p>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 rounded-2xl shadow-lg border border-amber-100 mb-6">
          <label className="flex items-center text-lg font-semibold text-amber-800 mb-4">
            <AlertTriangle className="mr-2 text-amber-600" size={20} />
            Autres Conditions Médicales
          </label>
          <textarea
            value={(formData.conditionsMedicales.autre as string) || ""}
            onChange={(e) =>
              updateFormData("conditionsMedicales", "autre", e.target.value)
            }
            className="w-full px-4 py-3 border-2 border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 resize-none bg-white/80 backdrop-blur-sm"
            rows={3}
            placeholder="Saisir toute autre condition médicale non listée ci-dessus..."
          />
        </div>

        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-2xl shadow-lg border border-green-100">
          <label className="flex items-center text-lg font-semibold text-green-800 mb-4">
            <Pill className="mr-2 text-green-600" size={20} />
            Médicaments Actuels
          </label>
          <textarea
            value={formData.medicaments}
            onChange={handleMedicamentsChange}
            className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 resize-none bg-white/80 backdrop-blur-sm"
            rows={4}
            placeholder="Liste complète des médicaments actuels (nom, dosage, fréquence)..."
          />
          <div className="mt-3 text-sm text-green-700 bg-green-100/50 p-3 rounded-lg">
            <p className="font-medium">💡 Conseil : Incluez tous les médicaments, compléments et traitements en cours</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicalConditionsStep;